import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import errorToast from "helpers/errorToast";
import { FormattedMessage } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { getQuoteRequestDetails } from "api";
import { useGetAssignedTeams } from "api/queries/quotation/useGetAssignedTeams";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as DeclineIcon } from "assets/images/iconsV2/slash-circle-01.svg";
import AssignTeamDialog from "pages/provider/QuotationDetail/AssignTeamDialog";
import QuotationDeclineDialog from "pages/provider/QuotationDetail/QuotationDeclineDialog";
import ReopenDialog from "pages/provider/QuotationDetail/ReopenDialog";
import ReleaseTeamDialog from "pages/provider/QuotationDetail/ReleaseTeamDialog";
import { IAssignTeam } from "pages/provider/QuotationDetail/QuotationType";

const SHIPMENT_STATUS = ["Delayed", "In transit", "Incomplete", "Delivered", "Auto-closed", "Booked", "Cancelled"];

function ProviderQuotationActionButtons() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [quoteRequestData, setQuoteRequestData] = useState<any>({});
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [unAssignTeam, setUnassignTeam] = useState<IAssignTeam>({});

  const { data: assignedTeams = [], refetch: refetchAssignedTeams } = useGetAssignedTeams({
    id: id!,
    config: { params: { already_assigned: true } },
  });

  const { data: assignableTeams = [], refetch: refetchAssignableTeams } = useGetAssignedTeams({
    id: id!,
    config: { params: { already_assigned: false } },
  });

  const { data: shipmentData, error: shipmentBasicError } = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { quotes_received, request_status: shipmentStatus } = shipmentData?.shipment || {};
  const { request_status, quoting_deadline_seconds_remaining } = quoteRequestData?.organization || {};
  const isQuotingDeadlineExpired = quoting_deadline_seconds_remaining <= 0;
  const isAssigned = assignedTeams.length > 0;
  const isShipmentBooked = SHIPMENT_STATUS.includes(shipmentStatus);
  const assignedTeam = assignedTeams?.[0];

  useEffect(() => {
    if (id) {
      // Get quote request detail
      getQuoteRequest();
    }
  }, []);

  const getQuoteRequest = () => {
    getQuoteRequestDetails(id)
      .then((res) => {
        if (res.data) {
          setQuoteRequestData(res.data);
        }
      })
      .catch((err) => errorToast(err));
  };

  const handleRedirectQuotationForm = () => {
    navigate(`/shipment/${id}/quotation/add`);
  };

  const handleShowAssignDialog = () => {
    setShowAssignDialog(true);
  };

  const handleShowDeclineDialog = () => {
    setShowDeclineDialog(true);
  };

  const handleShowReopenDialog = () => {
    setShowReopenDialog(true);
  };

  const handleShowReleaseDialog = (assignedTeam: IAssignTeam) => {
    setShowReleaseDialog(true);
    setUnassignTeam(assignedTeam);
  };

  return (
    <>
      <AssignTeamDialog
        isOpen={showAssignDialog}
        close={() => setShowAssignDialog(false)}
        assignableTeams={assignableTeams}
        getQuoteRequest={getQuoteRequest}
        refetchAssignedTeams={refetchAssignedTeams}
        refetchAssignableTeams={refetchAssignableTeams}
      />
      <QuotationDeclineDialog
        isOpen={showDeclineDialog}
        close={() => setShowDeclineDialog(false)}
        getQuoteRequest={getQuoteRequest}
      />
      <ReopenDialog isOpen={showReopenDialog} close={() => setShowReopenDialog(false)} getQuoteRequest={getQuoteRequest} />
      <ReleaseTeamDialog
        isOpen={showReleaseDialog}
        close={() => setShowReleaseDialog(false)}
        unAssignTeam={unAssignTeam}
        getQuoteRequest={getQuoteRequest}
        refetchAssignedTeams={refetchAssignedTeams}
        refetchAssignableTeams={refetchAssignableTeams}
      />
      <>
        {assignedTeam && (
          <OverlayTrigger
            key="bottom"
            placement="bottom"
            show={assignableTeams?.length === 0 ? false : undefined}
            overlay={
              <Tooltip id={`tooltip-${assignedTeam?.team_reference}`}>
                <strong>
                  <FormattedMessage
                    id="quote.releaseAssignedTeam"
                    values={{ name: assignedTeam?.team_name }}
                    defaultMessage="Release assigned team from this request. You can always assign it back."
                  />
                </strong>
              </Tooltip>
            }
          >
            <button
              type="button"
              className="cta-button gray"
              variant="primary"
              onClick={() => handleShowReleaseDialog(assignedTeam)}
              disabled={Boolean(
                assignableTeams?.length === 0 ||
                  isShipmentBooked ||
                  quotes_received ||
                  shipmentBasicError?.response?.status === 403,
              )}
            >
              <FormattedMessage
                id="quote.assignedTo"
                values={{ name: assignedTeam?.team_name }}
                defaultMessage="Assigned Team"
              />
            </button>
          </OverlayTrigger>
        )}
        {assignableTeams.length >= 1 && assignedTeams.length === 0 && (
          <button
            type="button"
            className="cta-button outline"
            onClick={handleShowAssignDialog}
            disabled={isShipmentBooked || quotes_received}
          >
            <FormattedMessage id="quote.assignToTeam" defaultMessage="Assign to a team" />
          </button>
        )}
        {!isQuotingDeadlineExpired && shipmentBasicError?.response?.status !== 403 && (
          <>
            {!["Uninvited", "Declined"].includes(request_status) && isAssigned && !isShipmentBooked && (
              <button type="button" className="cta-button outline" onClick={handleRedirectQuotationForm}>
                <FormattedMessage id="general.submit" defaultMessage="Submit" />
              </button>
            )}
            {request_status !== "Declined" && isAssigned && !quotes_received && !isShipmentBooked && (
              <button type="button" className="cta-button danger" onClick={handleShowDeclineDialog}>
                <DeclineIcon />
                <FormattedMessage id="general.decline" defaultMessage="Decline" />
              </button>
            )}
            {request_status === "Declined" && (
              <button type="button" className="cta-button outline" onClick={handleShowReopenDialog}>
                <FormattedMessage id="general.reopen" defaultMessage="Reopen" />
              </button>
            )}
          </>
        )}
      </>
    </>
  );
}

export default ProviderQuotationActionButtons;
